import { useQuery } from '@tanstack/react-query';
import { axiosGet, axiosGetV3 } from '../../api/axios-handler';

export type Tag = {
  id: number;
  is_background: boolean;
  is_default: boolean;
  is_required: boolean;
  is_visible: boolean;
  media?: string;
  name: string;
  sort_order?: number;
  tag_type: 'trait' | 'topic';
};

export type TagGroup = {
  children: TagGroup[];
  description?: string;
  id: number;
  key: string;
  name: string;
  parent_id?: number;
  tag_group_type: 'trait' | 'topic';
  tags: Tag[];
};

const getTagGroups = async (): Promise<TagGroup[]> => {
  return await axiosGetV3('/tag_groups/2026', {}).then((tagGroupsResponse) => tagGroupsResponse.data);
};

const getAllTagGroups = async (): Promise<TagGroup[]> => {
  return await axiosGet(`/tag_groups/?keys=all`, {}).then((tagGroupsResponse) => tagGroupsResponse.data);
};

export const useTagGroups = (fetchAll?: boolean, enabled = true) => {
  const { data, isLoading, error, refetch } = useQuery<TagGroup[]>(['tag_groups'], getTagGroups, {
    staleTime: Infinity,
    enabled,
  });

  // Separate query for all tag groups (used when searching by key)
  const {
    data: allTagGroupsData,
    isLoading: isLoadingAll,
    refetch: refetchAll,
  } = useQuery<TagGroup[]>(['tag_groups', 'all'], getAllTagGroups, {
    staleTime: Infinity,
    enabled: fetchAll || false, // Only fetch when explicitly enabled
  });

  const getParentByParentId = (parentId: number) => {
    return data?.find((tagGroup) => tagGroup.id === parentId);
  };

  const getTagGroupByTagId = (tagId: number) => {
    return data?.find((tagGroup) =>
      tagGroup.children
        ?.map((subjectTagGroup) => subjectTagGroup.tags?.map((tag) => tag.id))
        .flat()
        .includes(tagId),
    );
  };

  const getTagGroupByKey = (key: string) => {
    // Use allTagGroupsData if available (contains all tag groups), otherwise fall back to data
    const searchData = allTagGroupsData || data;
    if (!searchData || !key) return [];

    // Normalize the search key to uppercase for case-insensitive comparison
    const normalizedKey = key.toUpperCase();

    // Search recursively through all tag groups and their children
    const searchRecursive = (tagGroups: TagGroup[]): TagGroup[] => {
      const results: TagGroup[] = [];

      for (const tagGroup of tagGroups) {
        // Check if this tag group matches (case-insensitive)
        // Skip if tagGroup.key is null or undefined
        if (tagGroup.key && tagGroup.key.toUpperCase() === normalizedKey) {
          results.push(tagGroup);
        }

        // Recursively search children
        if (tagGroup.children && tagGroup.children.length > 0) {
          results.push(...searchRecursive(tagGroup.children));
        }
      }

      return results;
    };

    return searchRecursive(searchData);
  };

  const getTagGroupById = (id: number) => {
    // Use allTagGroupsData if available (contains all tag groups), otherwise fall back to data
    const searchData = allTagGroupsData || data;
    if (!searchData) return undefined;

    // Search recursively through all tag groups and their children
    const searchRecursive = (tagGroups: TagGroup[]): TagGroup | undefined => {
      for (const tagGroup of tagGroups) {
        if (tagGroup.id === id) {
          return tagGroup;
        }
        // Recursively search children
        if (tagGroup.children && tagGroup.children.length > 0) {
          const found = searchRecursive(tagGroup.children);
          if (found) return found;
        }
      }
      return undefined;
    };

    return searchRecursive(searchData);
  };

  const getTagGroupByName = (name: string) => {
    return data?.find((tagGroup) => tagGroup.name === name);
  };

  const formatTagsAndTagGroups = (tagGroups: number[], tags: number[]) => {
    /*
      validation to each list of tags and tag groups to remove items that aren't higher up in the tree
      e.g. if a tag is present, but the correspending subject isn't selected, that tag is removed here
      */
    const includedChallengeAreas = data?.filter((challengeArea) => tagGroups.includes(challengeArea.id));

    const tag_group_ids = tagGroups.filter(
      (id) =>
        includedChallengeAreas
          ?.map((challengeArea) => challengeArea.children.map((subject) => subject.id))
          .flat()
          .includes(id) || includedChallengeAreas?.map((area) => area.id).includes(id),
    );

    const tag_ids = tags.filter((tagId) => tag_group_ids.includes(getTagGroupByTagId(tagId)?.id ?? -1));

    return {
      tag_group_ids,
      tag_ids,
    };
  };

  return {
    data,
    isLoading,
    error,
    refetch,
    getTagGroupByKey,
    getParentByParentId,
    getTagGroupByName,
    getTagGroupByTagId,
    getTagGroupById,
    formatTagsAndTagGroups,
    allTagGroupsData,
    isLoadingAll,
    refetchAll,
  };
};
